import React from 'react';
import styled from 'styled-components';
import { motion as m } from 'framer-motion';
import { FiX, FiBell, FiClock } from 'react-icons/fi';

const ModalOverlay = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(m.div)`
  background: white;
  border-radius: 20px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  position: relative;
  margin-top: 60px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled(m.button)`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NotificationItem = styled(m.div)`
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  cursor: pointer;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const NotificationTitle = styled.h3`
  font-size: 16px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 600;
`;

const NotificationTime = styled.div`
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

interface NotificationsModalProps {
  onClose: () => void;
}

const mockNotifications = [
  {
    id: 1,
    title: 'New Enquiry Received',
    time: '2 minutes ago'
  },
  {
    id: 2,
    title: 'Profile Update Successful',
    time: '1 hour ago'
  },
  {
    id: 3,
    title: 'Welcome to Style Box!',
    time: '2 days ago'
  }
];

const NotificationsModal: React.FC<NotificationsModalProps> = ({ onClose }) => {
  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ x: 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 20, opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={e => e.stopPropagation()}
      >
        <Header>
          <Title>
            <FiBell /> Notifications
          </Title>
          <CloseButton
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiX />
          </CloseButton>
        </Header>

        <NotificationList>
          {mockNotifications.length > 0 ? (
            mockNotifications.map(notification => (
              <NotificationItem
                key={notification.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <NotificationTitle>{notification.title}</NotificationTitle>
                <NotificationTime>
                  <FiClock />
                  {notification.time}
                </NotificationTime>
              </NotificationItem>
            ))
          ) : (
            <EmptyState>
              <FiBell size={40} />
              <p>No notifications yet</p>
            </EmptyState>
          )}
        </NotificationList>
      </ModalContent>
    </ModalOverlay>
  );
};

export default NotificationsModal; 